import { db, auth } from '../utils/firebase';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { Achievement } from '../types/achievement';

// Statik Başarım Listesi
const ACHIEVEMENTS_LIST: Omit<Achievement, 'unlocked' | 'unlockedAt'>[] = [
    {
        id: 'first-plan',
        title: 'İlk Planını Yaptın',
        description: 'Yapay zekâ ile ilk kişisel seyahat planını oluşturdun.',
        category: 'profil',
        points: 100,
        icon: '🚀'
    },
    {
        id: 'weekend-explorer',
        title: 'Hafta Sonu Kaşifi',
        description: '3 farklı şehir için etkinlik veya rota sayfasını ziyaret ettin.',
        category: 'kesif',
        points: 150,
        icon: '🌍'
    },
    {
        id: 'community-supporter',
        title: 'Topluluk Destekçisi',
        description: 'Say-Hot’ı sosyal medyada paylaştın.',
        category: 'topluluk',
        points: 50,
        icon: '📣'
    },
    {
        id: 'event-hunter',
        title: 'Etkinlik Avcısı',
        description: '5 farklı etkinliği detay sayfasında inceledin.',
        category: 'kesif',
        points: 75,
        icon: '🎫'
    },
    {
        id: 'holiday-dna',
        title: 'Tatil DNA’nı Çözdün',
        description: 'Tatil DNA’nı anketini tamamlayarak profilini güçlendirdin.',
        category: 'profil',
        points: 200,
        icon: '🧬'
    }
];

export const getAchievements = async (): Promise<Achievement[]> => {
    const user = auth.currentUser;

    // Varsayılan olarak hepsi kilitli
    let achievements: Achievement[] = ACHIEVEMENTS_LIST.map(a => ({
        ...a,
        unlocked: false
    }));

    if (!user) {
        // Kullanıcı giriş yapmamışsa local storage kontrol edilebilir veya direkt kilitli döner
        return achievements;
    }

    try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
            const userData = userDoc.data();
            const unlockedAchievements = userData.achievements || []; // { achievementId: string, unlockedAt: Timestamp }[]

            achievements = achievements.map(achievement => {
                const unlockedData = unlockedAchievements.find((ua: any) => ua.achievementId === achievement.id);
                if (unlockedData) {
                    return {
                        ...achievement,
                        unlocked: true,
                        unlockedAt: unlockedData.unlockedAt?.toDate() || new Date()
                    };
                }
                return achievement;
            });
        }
    } catch (error) {
        console.error("Başarımlar getirilirken hata:", error);
    }

    return achievements;
};

export const unlockAchievement = async (achievementId: string): Promise<boolean> => {
    const user = auth.currentUser;
    if (!user) return false;

    const achievement = ACHIEVEMENTS_LIST.find(a => a.id === achievementId);
    if (!achievement) return false;

    try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        // Kullanıcı dökümanı yoksa oluştur
        if (!userDoc.exists()) {
            await setDoc(userRef, { achievements: [] }, { merge: true });
        }

        // Zaten açık mı kontrol et
        const userData = userDoc.exists() ? userDoc.data() : { achievements: [] };
        const alreadyUnlocked = userData.achievements?.some((ua: any) => ua.achievementId === achievementId);

        if (alreadyUnlocked) {
            return false; // Zaten açık
        }

        // Yeni başarımı ekle
        await updateDoc(userRef, {
            achievements: arrayUnion({
                achievementId: achievementId,
                unlockedAt: Timestamp.now()
            }),
            totalPoints: (userData.totalPoints || 0) + achievement.points
        });

        return true; // Yeni açıldı
    } catch (error) {
        console.error("Başarım açılırken hata:", error);
        return false;
    }
};

export const getTotalPoints = async (): Promise<number> => {
    const user = auth.currentUser;
    if (!user) return 0;

    try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
            return userDoc.data().totalPoints || 0;
        }
    } catch (error) {
        console.error("Puan getirilirken hata:", error);
    }
    return 0;
};

export const trackUniqueStat = async (statName: string, value: string, threshold: number, achievementId: string): Promise<boolean> => {
    const user = auth.currentUser;
    if (!user) return false;

    try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        // Kullanıcı dökümanı yoksa oluştur
        if (!userDoc.exists()) {
            await setDoc(userRef, { stats: { [statName]: [value] } }, { merge: true });
            return false; // İlk değer, threshold muhtemelen 1'den büyüktür
        }

        const userData = userDoc.data();
        const currentStats = userData.stats || {};
        const currentValues: string[] = currentStats[statName] || [];

        // Zaten varsa işlem yapma
        if (currentValues.includes(value)) {
            return false;
        }

        // Yeni değeri ekle
        const newValues = [...currentValues, value];
        await updateDoc(userRef, {
            [`stats.${statName}`]: arrayUnion(value)
        });

        // Eşik değerine ulaşıldı mı kontrol et
        if (newValues.length >= threshold) {
            return await unlockAchievement(achievementId);
        }

        return false;
    } catch (error) {
        console.error(`İstatistik (${statName}) güncellenirken hata:`, error);
        return false;
    }
};

export const trackAction = async (actionName: string, achievementId: string): Promise<boolean> => {
    const user = auth.currentUser;
    if (!user) return false;

    try {
        const userRef = doc(db, 'users', user.uid);

        // Basitçe bu aksiyonun yapıldığını işaretle (boolean veya count olabilir, şimdilik boolean yeterli)
        await setDoc(userRef, {
            stats: { [actionName]: true }
        }, { merge: true });

        // Direkt başarımı açmayı dene
        return await unlockAchievement(achievementId);
    } catch (error) {
        console.error(`Aksiyon (${actionName}) izlenirken hata:`, error);
        return false;
    }
};
